import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProjectGallerySlider from "./ProjectGallerySlider";
import { backdropFade, scalePop, staggerContainer, staggerItem } from "../utils/motionVariants";
import "./ProjectModal.css";

export default function ProjectModal({ project, onClose }) {
  useEffect(() => {
    document.body.style.overflow = project ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [project]);

  useEffect(() => {
    if (!project) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [project, onClose]);

  const gallery = Array.isArray(project?.gallery) ? project.gallery : [];
  const sliderImages = gallery.length
    ? gallery
    : project?.cover
      ? [{ src: project.cover, alt: project.title }]
      : [];

  const timeframeLabel = project?.timeframe
    ? `${project.timeframe.start}${project.timeframe.end ? ` - ${project.timeframe.end}` : ""} • ${
        project.timeframe.status
      }`
    : null;
  const metaItems = [timeframeLabel, project?.domain?.length ? project.domain.join(" • ") : null].filter(Boolean);

  const listSection = (title, items = []) => {
    const safeItems = Array.isArray(items) ? items : [];
    return (
      <motion.div className="project-modal-card" key={title} variants={staggerItem}>
        <h4>{title}</h4>
        <ul>
          {safeItems.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </motion.div>
    );
  };

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          className="project-modal-backdrop"
          onClick={onClose}
          variants={backdropFade}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="project-modal-shell"
            onClick={(e) => e.stopPropagation()}
            variants={scalePop}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.button
              type="button"
              className="project-modal-close"
              onClick={onClose}
              aria-label="Close project details"
              whileHover={{ scale: 1.15, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            >
              &times;
            </motion.button>
            {sliderImages.length > 0 && <ProjectGallerySlider images={sliderImages} className="project-modal-slider" />}

            <div className="project-modal-body">
              <motion.div
                className="project-modal-header"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.4 }}
              >
                <p className="eyebrow">{project.clientOrOrg}</p>
                <h3>{project.title}</h3>
                <p className="muted">{project.oneLiner}</p>
                {metaItems.length > 0 && (
                  <div className="project-modal-meta">
                    {metaItems.map((item) => (
                      <span key={item}>{item}</span>
                    ))}
                  </div>
                )}
                <p className="project-modal-tags">
                  <strong>Tech:</strong> {project.tech?.join(", ") || "Details on request"}
                </p>
                <div className="project-modal-links">
                  {project.links?.live && (
                    <motion.a
                      href={project.links.live}
                      target="_blank"
                      rel="noreferrer"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      Visit live site
                    </motion.a>
                  )}
                  {project.links?.demo && (
                    <motion.a
                      href={project.links.demo}
                      target="_blank"
                      rel="noreferrer"
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      DEMO
                    </motion.a>
                  )}
                  {project.links?.apk && (
                    <motion.a
                      href={project.links.apk}
                      download
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.96 }}
                    >
                      Download APK
                    </motion.a>
                  )}
                </div>
              </motion.div>

              <motion.div
                className="project-modal-grid"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {listSection("Impact", project.impact)}
                {listSection("Responsibilities", project.responsibilities)}
                {listSection("Key Features", project.features)}
                {listSection("Architecture", project.architecture)}
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
